/**
 * Frame timing statistics with on-screen display.
 *
 * Tracks frame times and displays FPS, frame time, and histogram overlay.
 * Uses a separate DOM canvas overlay since the main canvas uses WebGL.
 */

const HISTORY_SIZE = 120; // ~2 seconds at 60fps
const HISTOGRAM_BINS = 20;
const HISTOGRAM_MAX_MS = 50; // Cap histogram at 50ms

export class FrameStats {
  private enabled: boolean = false;
  private frameStart: number = 0;

  // Ring buffer for frame times (no allocations during runtime)
  private frameTimes: Float64Array = new Float64Array(HISTORY_SIZE);
  private frameTimesHead: number = 0;
  private frameTimesCount: number = 0;

  // Ring buffer for timestamps (for actual FPS - last 1 second worth)
  private frameTimestamps: Float64Array = new Float64Array(HISTORY_SIZE);
  private timestampsHead: number = 0;
  private timestampsCount: number = 0;

  private histogramBins: Uint32Array = new Uint32Array(HISTOGRAM_BINS);

  // DOM overlay canvas (positioned over the WebGL canvas)
  private overlayCanvas: HTMLCanvasElement | null = null;
  private overlayCtx: CanvasRenderingContext2D | null = null;
  private targetCanvas: HTMLCanvasElement | null = null;

  constructor() {
    // Start disabled by default
    this.enabled = false;
  }

  /**
   * Toggle stats display on/off.
   */
  toggle(): void {
    this.enabled = !this.enabled;
  }

  /**
   * Check if stats are enabled.
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Call at the start of each frame.
   */
  beginFrame(): void {
    this.frameStart = performance.now();
  }

  /**
   * Call at the end of each frame.
   */
  endFrame(): void {
    const now = performance.now();
    const frameTime = now - this.frameStart;

    // Add frame time to ring buffer
    this.frameTimes[this.frameTimesHead] = frameTime;
    this.frameTimesHead = (this.frameTimesHead + 1) % HISTORY_SIZE;
    if (this.frameTimesCount < HISTORY_SIZE) this.frameTimesCount++;

    // Add timestamp to ring buffer
    this.frameTimestamps[this.timestampsHead] = now;
    this.timestampsHead = (this.timestampsHead + 1) % HISTORY_SIZE;
    if (this.timestampsCount < HISTORY_SIZE) this.timestampsCount++;

    // Update histogram
    this.updateHistogram();
  }

  /**
   * Update histogram bins from frame time history.
   */
  private updateHistogram(): void {
    // Reset bins
    this.histogramBins.fill(0);

    // Bin frame times from ring buffer
    const binWidth = HISTOGRAM_MAX_MS / HISTOGRAM_BINS;
    for (let i = 0; i < this.frameTimesCount; i++) {
      const ft = this.frameTimes[i];
      const bin = Math.min(HISTOGRAM_BINS - 1, Math.floor(ft / binWidth));
      this.histogramBins[bin]++;
    }
  }

  /**
   * Get current stats for external use.
   */
  getStats(): { fps: number; frameTime: number; histogram: number[] } {
    // Calculate average frame time
    let sum = 0;
    for (let i = 0; i < this.frameTimesCount; i++) {
      sum += this.frameTimes[i];
    }
    const avgFrameTime = this.frameTimesCount > 0 ? sum / this.frameTimesCount : 0;

    // Calculate actual FPS from timestamps in the last second
    const now = performance.now();
    const oneSecondAgo = now - 1000;
    let framesInLastSecond = 0;
    for (let i = 0; i < this.timestampsCount; i++) {
      if (this.frameTimestamps[i] >= oneSecondAgo) {
        framesInLastSecond++;
      }
    }

    return {
      fps: framesInLastSecond,
      frameTime: avgFrameTime,
      histogram: Array.from(this.histogramBins),
    };
  }

  /**
   * Draw the stats overlay.
   * Creates a DOM canvas overlay positioned over the WebGL canvas.
   */
  draw(targetCanvas: HTMLCanvasElement): void {
    if (!this.enabled || this.frameTimes.length === 0) {
      // Hide overlay if disabled
      if (this.overlayCanvas) {
        this.overlayCanvas.style.display = 'none';
      }
      return;
    }

    // Create or update overlay canvas
    if (!this.overlayCanvas || this.targetCanvas !== targetCanvas) {
      this.createOverlayCanvas(targetCanvas);
    }

    // Update size if needed
    if (
      this.overlayCanvas &&
      (this.overlayCanvas.width !== targetCanvas.width ||
        this.overlayCanvas.height !== targetCanvas.height)
    ) {
      this.overlayCanvas.width = targetCanvas.width;
      this.overlayCanvas.height = targetCanvas.height;
    }

    const ctx = this.overlayCtx;
    if (!ctx || !this.overlayCanvas) return;

    // Show overlay
    this.overlayCanvas.style.display = 'block';

    // Get stats
    const stats = this.getStats();
    const lastIdx = (this.frameTimesHead - 1 + HISTORY_SIZE) % HISTORY_SIZE;
    const lastFrameTime = this.frameTimesCount > 0 ? this.frameTimes[lastIdx] : 0;

    // Layout constants
    const padding = 10;
    const lineHeight = 18;
    const histWidth = 200;
    const histHeight = 60;
    const barWidth = histWidth / HISTOGRAM_BINS;

    // Position in top-right
    const x = this.overlayCanvas.width - histWidth - padding * 2;
    const y = padding;

    // Clear entire overlay
    ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(
      x - padding,
      y - padding,
      histWidth + padding * 2,
      histHeight + lineHeight * 3 + padding * 2
    );

    // Text stats
    ctx.font = '14px monospace';
    ctx.fillStyle = '#fff';
    ctx.fillText(
      `FPS: ${stats.fps.toFixed(0)} | ${targetCanvas.width}x${targetCanvas.height}`,
      x,
      y + lineHeight
    );
    ctx.fillText(
      `Frame: ${lastFrameTime.toFixed(2)}ms (avg: ${stats.frameTime.toFixed(2)}ms)`,
      x,
      y + lineHeight * 2
    );

    // Histogram
    const histY = y + lineHeight * 2.5;
    let maxBin = 1;
    for (let i = 0; i < HISTOGRAM_BINS; i++) {
      if (this.histogramBins[i] > maxBin) maxBin = this.histogramBins[i];
    }

    // Draw bars
    for (let i = 0; i < HISTOGRAM_BINS; i++) {
      const barHeight = (this.histogramBins[i] / maxBin) * histHeight;
      const binMs = (i + 0.5) * (HISTOGRAM_MAX_MS / HISTOGRAM_BINS);

      // Color based on frame time (green -> yellow -> red)
      let color: string;
      if (binMs < 16.67) {
        color = '#4ade80'; // Green - 60fps+
      } else if (binMs < 33.33) {
        color = '#fbbf24'; // Yellow - 30-60fps
      } else {
        color = '#ef4444'; // Red - <30fps
      }

      ctx.fillStyle = color;
      ctx.fillRect(x + i * barWidth, histY + histHeight - barHeight, barWidth - 1, barHeight);
    }

    // Histogram labels
    ctx.fillStyle = '#888';
    ctx.font = '10px monospace';
    ctx.fillText('0ms', x, histY + histHeight + 12);
    ctx.fillText(`${HISTOGRAM_MAX_MS}ms`, x + histWidth - 25, histY + histHeight + 12);

    // Draw 16.67ms line (60fps target)
    const target60Line = (16.67 / HISTOGRAM_MAX_MS) * histWidth;
    ctx.strokeStyle = '#4ade80';
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(x + target60Line, histY);
    ctx.lineTo(x + target60Line, histY + histHeight);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  /**
   * Create the overlay canvas and position it over the target.
   */
  private createOverlayCanvas(targetCanvas: HTMLCanvasElement): void {
    // Remove old overlay if exists
    if (this.overlayCanvas) {
      this.overlayCanvas.remove();
    }

    this.targetCanvas = targetCanvas;
    this.overlayCanvas = document.createElement('canvas');
    this.overlayCanvas.width = targetCanvas.width;
    this.overlayCanvas.height = targetCanvas.height;
    this.overlayCanvas.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    `;

    // Insert overlay after target canvas (requires parent to have position: relative)
    const parent = targetCanvas.parentElement;
    if (parent) {
      // Ensure parent has relative positioning
      const parentStyle = getComputedStyle(parent);
      if (parentStyle.position === 'static') {
        parent.style.position = 'relative';
      }
      parent.appendChild(this.overlayCanvas);
    }

    this.overlayCtx = this.overlayCanvas.getContext('2d');
  }

  /**
   * Clean up the overlay canvas.
   */
  dispose(): void {
    if (this.overlayCanvas) {
      this.overlayCanvas.remove();
      this.overlayCanvas = null;
      this.overlayCtx = null;
      this.targetCanvas = null;
    }
  }
}
